import { useState } from 'react'
import { Button, Dropdown, Input, Label } from '@heroui/react'
import { ApiError, api } from '../../lib/api'
import { explain } from '../../lib/staffErrors'
import { t, type StaffLanguage } from '../../lib/staffText'
import { Icon } from '../../ui/icons'
import { useStaffText } from '../../ui/StaffText'
import { InnerPage, type Crumb } from '../settingsKit'

/** A refusal in the reader's language; PROFILE_IN_USE carries n as a string, which would always pick the first plural form. */
export function refusalText(err: unknown, lang: StaffLanguage): string {
  if (err instanceof ApiError && err.code === 'PROFILE_IN_USE' && err.args?.n !== undefined) {
    return t('errors.PROFILE_IN_USE', lang, { n: Number(err.args.n) })
  }
  return explain(err, lang, 'common.unreachable')
}

/** An operation profile's More menu (HeroUI Dropdown): rename, duplicate, export. */
export function ProfileMenu({
  profileId,
  readOnly,
  onRename,
  onDuplicate,
}: {
  profileId: string
  readOnly: boolean
  onRename: () => void
  onDuplicate: () => void
}) {
  const { say } = useStaffText()
  return (
    <Dropdown>
      <Button isIconOnly variant="ghost" aria-label={say('admin.publish.more')}>
        <Icon name="more" size={20} />
      </Button>
      <Dropdown.Popover placement="bottom end">
        <Dropdown.Menu
          onAction={(key) => {
            if (key === 'rename') onRename()
            else if (key === 'duplicate') onDuplicate()
          }}
        >
          {!readOnly && (
            <Dropdown.Item id="rename" textValue={say('profiles.list.rename')}>
              <Icon name="edit" size={16} />
              <Label>{say('profiles.list.rename')}</Label>
            </Dropdown.Item>
          )}
          {!readOnly && (
            <Dropdown.Item id="duplicate" textValue={say('profiles.list.duplicate')}>
              <Icon name="copy" size={16} />
              <Label>{say('profiles.list.duplicate')}</Label>
            </Dropdown.Item>
          )}
          <Dropdown.Item id="export" textValue={say('profiles.list.export')} href={api.exportOperationProfileUrl(profileId)} download>
            <Icon name="download" size={16} />
            <Label>{say('profiles.list.export')}</Label>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown>
  )
}

/** Names an operation profile: adding, renaming and duplicating one. */
export function NameSheet({
  title,
  submitLabel,
  initial,
  hideDescription,
  onClose,
  trail,
  onSave,
}: {
  title: string
  submitLabel: string
  initial: { name: string; description: string }
  hideDescription?: boolean
  onClose: () => void
  /** The path back: the profiles list, and the profile when one is open. */
  trail: Crumb[]
  onSave: (v: { name: string; description: string }) => Promise<void>
}) {
  const { say, lang } = useStaffText()
  const [name, setName] = useState(initial.name)
  const [description, setDescription] = useState(initial.description)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      await onSave({ name: name.trim(), description: description.trim() })
    } catch (err) {
      setError(refusalText(err, lang))
    } finally {
      setBusy(false)
    }
  }

  return (
    <InnerPage
      before={trail}
      title={title}
      onClose={onClose}
      onSubmit={() => void submit()}
      submitLabel={submitLabel}
      valid={name.trim() !== ''}
      busy={busy}
      error={error}
    >
      <label className="field">
        <span>{say('common.name')}</span>
        <Input fullWidth value={name} maxLength={120} required onChange={(e) => setName(e.target.value)} />
      </label>
      {!hideDescription && (
        <label className="field">
          <span>{say('profiles.list.description')}</span>
          <Input fullWidth value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
      )}
    </InnerPage>
  )
}
